import ResponsiveWrapper from "../ResponsiveWrapper/ResponsiveWrapper";
import { scaleLinear, scaleTime } from "@visx/scale";
import { AreaClosed, LinePath } from "@visx/shape";
import { Group } from "@visx/group";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { LinearGradient } from "@visx/gradient";
import { formatCurrency } from "../../utils/format";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";
import { useUserPreferences } from "../../contexts/UserPreferencesContext";
import { useScreenHeight, getChartHeight } from "../../hooks/useScreenHeight";
import { curveMonotoneX } from "@visx/curve";
import { localPoint } from "@visx/event";
import { bisector } from "d3-array";

const margin = { top: 20, right: 20, bottom: 25, left: 55 };

const AreaChart = ({
	data,
	translate,
	isMonthly = false,
	averages,
	displayMetrics,
}) => {
	const { locale } = useUserPreferences();
	const screenHeight = useScreenHeight();
	const chartHeight = getChartHeight(screenHeight);
	const currentYear = new Date().getFullYear();

	// Tooltip setup
	const {
		tooltipData,
		tooltipLeft,
		tooltipTop,
		tooltipOpen,
		showTooltip,
		hideTooltip,
	} = useTooltip();
	const { containerRef, TooltipInPortal } = useTooltipInPortal({
		scroll: true,
	});

	// Process data for projections (similar to LineChart logic)
	const processedData = data.map(yearData => {
		if (!isMonthly) {
			// For yearly data, handle projections like LineChart
			const isCurrentYear = yearData.year === currentYear;
			const yearAverages = averages?.[yearData.year];
			const yearDisplayMetrics = displayMetrics?.[yearData.year];

			if (isCurrentYear && yearAverages && yearDisplayMetrics) {
				// Use projected values for current year
				const projectedIncome = (yearAverages.income || 0) * 12;
				const projectedExpenses = (yearAverages.expenses || 0) * 12;
				const projectedBalance = projectedIncome - projectedExpenses;

				return {
					...yearData,
					totalIncome: projectedIncome,
					totalExpenses: projectedExpenses,
					balance: projectedBalance,
					projectedIncome,
					projectedExpenses,
					projectedBalance,
					isCurrentYear: true,
					monthsForAverage: yearDisplayMetrics.monthsForAverage || 1,
				};
			}

			return {
				...yearData,
				projectedIncome: yearData.totalIncome,
				projectedExpenses: yearData.totalExpenses,
				projectedBalance: yearData.balance,
				isCurrentYear: false,
			};
		}

		// For monthly data, keep original logic
		return {
			...yearData,
			projectedIncome: yearData.totalIncome,
			projectedExpenses: yearData.totalExpenses,
			projectedBalance: yearData.balance,
			isCurrentYear: false,
		};
	});

	// Accessor functions for data (moved inside component to access isMonthly)
	const getDateValue = d => {
		if (isMonthly) {
			const [year, month] = d.month.split("-");
			return new Date(parseInt(year), parseInt(month) - 1, 1);
		} else {
			return new Date(d.year, 0, 1);
		}
	};

	const getBalance = d => d.balance;

	// Bisector for tooltip positioning
	const bisectDate = bisector(d => getDateValue(d)).left;

	const renderChart = ({ width, height }) => {
		if (width === 0 || height === 0) return null;

		const xMax = width - margin.left - margin.right;
		const yMax = height - margin.top - margin.bottom;

		// Create scales
		const xScale = scaleTime({
			domain: [
				getDateValue(processedData[0]),
				getDateValue(processedData[processedData.length - 1]),
			],
			range: [0, xMax],
		});

		const balanceValues = processedData.map(d => d.balance);
		const minBalance = Math.min(...balanceValues);
		const maxBalance = Math.max(...balanceValues);

		// Create a scale proportional to the actual data
		// Add a small padding (5%) so that the points don't touch the edges
		const padding = Math.max(Math.abs(maxBalance - minBalance) * 0.05, 100); // minimum padding of 100
		const yScale = scaleLinear({
			domain: [minBalance - padding, maxBalance + padding],
			nice: true,
			range: [yMax, 0],
		});

		// Find the current and previous years for the projection line
		const currentYearData = processedData.find(d => d.isCurrentYear);
		const currentYearIndex = processedData.findIndex(d => d.isCurrentYear);
		const previousYearData =
			currentYearIndex > 0 ? processedData[currentYearIndex - 1] : null;

		// Handle mouse events for tooltip
		const handleMouseMove = event => {
			const { x } = localPoint(event) || { x: 0 };
			const x0 = xScale.invert(x - margin.left);
			const index = bisectDate(processedData, x0, 1);
			const d0 = processedData[index - 1];
			const d1 = processedData[index];
			let d = d0;

			if (d1 && d0) {
				d =
					x0.valueOf() - getDateValue(d0).valueOf() >
					getDateValue(d1).valueOf() - x0.valueOf()
						? d1
						: d0;
			}

			if (d) {
				showTooltip({
					tooltipData: d,
					tooltipLeft: xScale(getDateValue(d)) + margin.left,
					tooltipTop: yScale(getBalance(d)) + margin.top,
				});
			}
		};

		const zeroLineY = yScale(0);

		return (
			<svg width={width} height={height}>
				<defs>
					{/* Define gradients for positive and negative areas - Fixed IDs */}
					<LinearGradient
						id={`positive-gradient-${isMonthly ? "monthly" : "yearly"}`}
						from="rgba(34, 197, 94, 0.6)"
						to="rgba(34, 197, 94, 0.1)"
						fromOpacity={0.6}
						toOpacity={0.1}
					/>
					<LinearGradient
						id={`negative-gradient-${isMonthly ? "monthly" : "yearly"}`}
						from="rgba(239, 68, 68, 0.6)"
						to="rgba(239, 68, 68, 0.1)"
						fromOpacity={0.6}
						toOpacity={0.1}
					/>

					{/* Mask for positive area (above zero line) */}
					<mask id={`positive-mask-${isMonthly ? "monthly" : "yearly"}`}>
						<rect width={xMax} height={zeroLineY} fill="white" />
					</mask>

					{/* Mask for negative area (below zero line) */}
					<mask id={`negative-mask-${isMonthly ? "monthly" : "yearly"}`}>
						<rect
							y={zeroLineY}
							width={xMax}
							height={yMax - zeroLineY}
							fill="white"
						/>
					</mask>
				</defs>

				<Group top={margin.top} left={margin.left}>
					{/* Y-axis */}
					<AxisLeft
						scale={yScale}
						stroke="var(--text-color)"
						tickLabelProps={() => ({
							fill: "var(--text-color)",
							fontSize: 11,
							textAnchor: "end",
						})}
						numTicks={8}
						tickLineProps={{
							stroke: "var(--text-color)",
							strokeWidth: 1,
						}}
					/>

					{/* X-axis */}
					<AxisBottom
						scale={xScale}
						top={yMax}
						stroke="var(--text-color)"
						tickFormat={date => {
							if (isMonthly) {
								return date.toLocaleDateString(locale, {
									month: "short",
									year: processedData.length > 12 ? "2-digit" : undefined,
								});
							} else {
								return date.getFullYear().toString();
							}
						}}
						tickLabelProps={() => ({
							fill: "var(--text-color)",
							fontSize: 11,
							textAnchor: "middle",
						})}
						numTicks={Math.min(processedData.length, isMonthly ? 6 : 10)}
						axisLineProps={{
							stroke: "var(--text-color)",
							strokeWidth: 1,
						}}
						tickLineProps={{
							stroke: "var(--text-color)",
							strokeWidth: 1,
						}}
					/>

					{/* Zero line - only if 0 is visible */}
					{minBalance <= 0 && maxBalance >= 0 && (
						<line
							x1={0}
							y1={zeroLineY}
							x2={xMax}
							y2={zeroLineY}
							stroke="var(--text-color)"
							strokeWidth={1}
							strokeDasharray="2,2"
							opacity={0.5}
						/>
					)}

					{/* Split data into actual and projected for rendering */}
					{(() => {
						if (!isMonthly && currentYearData && previousYearData) {
							// For yearly data with projections
							const actualData = processedData.filter(d => !d.isCurrentYear);
							const projectionData = [previousYearData, currentYearData];

							return (
								<>
									{/* Actual data area and line */}
									{actualData.length > 0 && (
										<>
											{/* Positive area (masked) for actual data */}
											<AreaClosed
												data={actualData}
												x={d => xScale(getDateValue(d))}
												y={d => yScale(getBalance(d))}
												yScale={yScale}
												strokeWidth={0}
												fill={`url(#positive-gradient-${
													isMonthly ? "monthly" : "yearly"
												})`}
												curve={curveMonotoneX}
												mask={`url(#positive-mask-${
													isMonthly ? "monthly" : "yearly"
												})`}
											/>

											{/* Negative area (masked) for actual data */}
											<AreaClosed
												data={actualData}
												x={d => xScale(getDateValue(d))}
												y={d => yScale(getBalance(d))}
												yScale={yScale}
												strokeWidth={0}
												fill={`url(#negative-gradient-${
													isMonthly ? "monthly" : "yearly"
												})`}
												curve={curveMonotoneX}
												mask={`url(#negative-mask-${
													isMonthly ? "monthly" : "yearly"
												})`}
											/>

											{/* Positive line (masked) for actual data */}
											<LinePath
												data={actualData}
												x={d => xScale(getDateValue(d))}
												y={d => yScale(getBalance(d))}
												strokeWidth={3}
												stroke="var(--green)"
												curve={curveMonotoneX}
												fill="none"
												mask={`url(#positive-mask-${
													isMonthly ? "monthly" : "yearly"
												})`}
											/>

											{/* Negative line (masked) for actual data */}
											<LinePath
												data={actualData}
												x={d => xScale(getDateValue(d))}
												y={d => yScale(getBalance(d))}
												strokeWidth={3}
												stroke="var(--warning)"
												curve={curveMonotoneX}
												fill="none"
												mask={`url(#negative-mask-${
													isMonthly ? "monthly" : "yearly"
												})`}
											/>
										</>
									)}

									{/* Projection line for current year (dashed) */}
									<LinePath
										data={projectionData}
										x={d => xScale(getDateValue(d))}
										y={d => yScale(getBalance(d))}
										strokeWidth={2}
										stroke={
											currentYearData.balance >= 0
												? "var(--green)"
												: "var(--warning)"
										}
										strokeDasharray="5,5"
										curve={curveMonotoneX}
										fill="none"
										opacity={0.8}
									/>
								</>
							);
						} else {
							// For monthly data or yearly data without projections
							return (
								<>
									{/* Positive area (masked) */}
									<AreaClosed
										data={processedData}
										x={d => xScale(getDateValue(d))}
										y={d => yScale(getBalance(d))}
										yScale={yScale}
										strokeWidth={0}
										fill={`url(#positive-gradient-${
											isMonthly ? "monthly" : "yearly"
										})`}
										curve={curveMonotoneX}
										mask={`url(#positive-mask-${
											isMonthly ? "monthly" : "yearly"
										})`}
									/>

									{/* Negative area (masked) */}
									<AreaClosed
										data={processedData}
										x={d => xScale(getDateValue(d))}
										y={d => yScale(getBalance(d))}
										yScale={yScale}
										strokeWidth={0}
										fill={`url(#negative-gradient-${
											isMonthly ? "monthly" : "yearly"
										})`}
										curve={curveMonotoneX}
										mask={`url(#negative-mask-${
											isMonthly ? "monthly" : "yearly"
										})`}
									/>

									{/* Positive line (masked) */}
									<LinePath
										data={processedData}
										x={d => xScale(getDateValue(d))}
										y={d => yScale(getBalance(d))}
										strokeWidth={3}
										stroke="var(--green)"
										curve={curveMonotoneX}
										fill="none"
										mask={`url(#positive-mask-${
											isMonthly ? "monthly" : "yearly"
										})`}
									/>

									{/* Negative line (masked) */}
									<LinePath
										data={processedData}
										x={d => xScale(getDateValue(d))}
										y={d => yScale(getBalance(d))}
										strokeWidth={3}
										stroke="var(--warning)"
										curve={curveMonotoneX}
										fill="none"
										mask={`url(#negative-mask-${
											isMonthly ? "monthly" : "yearly"
										})`}
									/>
								</>
							);
						}
					})()}

					{/* Data points */}
					{processedData.map((d, i) => (
						<circle
							key={`point-${i}`}
							cx={xScale(getDateValue(d))}
							cy={yScale(getBalance(d))}
							r={4}
							fill={d.balance >= 0 ? "var(--green)" : "var(--warning)"}
							stroke="white"
							strokeWidth={2}
							style={{ cursor: "pointer" }}
							opacity={1}
						/>
					))}

					{/* Invisible overlay for mouse events */}
					<rect
						width={xMax}
						height={yMax}
						fill="transparent"
						onMouseMove={handleMouseMove}
						onMouseLeave={hideTooltip}
						style={{ cursor: "crosshair" }}
					/>

					{/* Legend for current year projections */}
					{currentYearData && !isMonthly && (
						<g>
							<line
								x1={xMax - 25}
								y1={5}
								x2={xMax}
								y2={5}
								stroke="var(--text-color)"
								strokeWidth={3}
							/>
							<text
								x={xMax - 30}
								y={8}
								fontSize={10}
								fill="var(--text-color)"
								opacity={0.8}
								textAnchor="end"
							>
								{translate("analysis.legend.actual")}
							</text>

							<line
								x1={xMax - 25}
								y1={20}
								x2={xMax}
								y2={20}
								stroke="var(--text-color)"
								strokeWidth={2}
								strokeDasharray="5,5"
								opacity={0.6}
							/>
							<text
								x={xMax - 30}
								y={23}
								fontSize={10}
								fill="var(--text-color)"
								opacity={0.8}
								textAnchor="end"
							>
								{translate("analysis.legend.projected")}
							</text>
						</g>
					)}
				</Group>
			</svg>
		);
	};

	return (
		<div
			className="chart-box"
			style={{ height: `${chartHeight}px` }}
			ref={containerRef}
		>
			<ResponsiveWrapper fallbackHeight={chartHeight}>
				{renderChart}
			</ResponsiveWrapper>
			{tooltipOpen && tooltipData && (
				<TooltipInPortal
					top={tooltipTop}
					left={tooltipLeft}
					className="chart-tooltip"
				>
					<div className="tooltip-header">
						{isMonthly ? tooltipData.month : tooltipData.year}
						{tooltipData.isCurrentYear && (
							<span className="tooltip-months">
								{" "}
								(
								{translate("analysis.tooltip.basedOnMonths", {
									count: tooltipData.monthsForAverage,
								})}
								)
							</span>
						)}
					</div>
					<div
						className="tooltip-category"
						style={{
							color:
								tooltipData.balance >= 0 ? "var(--green)" : "var(--warning)",
						}}
					>
						{tooltipData.isCurrentYear ? (
							<span
								className="tooltip-projection"
								style={{
									color:
										tooltipData.balance >= 0
											? "var(--green)"
											: "var(--warning)",
								}}
							>
								{formatCurrency(tooltipData.balance, locale)}
							</span>
						) : (
							<span>{formatCurrency(tooltipData.balance, locale)}</span>
						)}
					</div>
					<div className="tooltip-details">
						<div>
							{translate("analysis.income")}{" "}
							{tooltipData.isCurrentYear ? (
								<span className="tooltip-projection">
									{formatCurrency(tooltipData.totalIncome, locale)}
								</span>
							) : (
								<span>{formatCurrency(tooltipData.totalIncome, locale)}</span>
							)}
						</div>
						<div>
							{translate("analysis.expenses")}{" "}
							{tooltipData.isCurrentYear ? (
								<span className="tooltip-projection">
									{formatCurrency(tooltipData.totalExpenses, locale)}
								</span>
							) : (
								<span>{formatCurrency(tooltipData.totalExpenses, locale)}</span>
							)}
						</div>
					</div>
				</TooltipInPortal>
			)}
		</div>
	);
};

export default AreaChart;
